// Port of Flutter's userScreen.dart — the "Me" tab, titled "Account Controls".
//
// A single column of rounded ButtonColor tiles, capped at 480 wide and centred.
// Two of them are gated on flags from the user record, exactly as in the Dart:
//   AllowTesting     == 1 -> "Test Device"   (open any device by Unique Number)
//   AllowTestingTuya == 1 -> "Scan Devices"
//
// The Dart's "Sync now (local DB)" tile is here too, master users only. It backs
// onto the SQLite offline cache (core/db/*) and reports row counts, which is the
// only way to tell the cache actually filled — a working cache and a working
// network look identical from the UI.

import React, { useEffect, useState } from 'react';
import {
  Box, ListItemButton, ListItemText, Avatar, useTheme, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
} from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DocumentScannerSharpIcon from '@mui/icons-material/DocumentScannerSharp';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import InfoIcon from '@mui/icons-material/Info';
import SyncIcon from '@mui/icons-material/Sync';
import WifiIcon from '@mui/icons-material/Wifi';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../../components/MobileAppBar';
import { useApp } from '../../../../core/state/AppContext';
import { useMessage } from '../../components/MessageDialog';
import { fullSync, tableCounts, lastSyncedAt } from '../../../../core/db/syncService';
import { dbSupported } from '../../../../core/db/schema';
import {
  isManualOffline, setManual, hasNoNetwork, onOfflineChange,
} from '../../../../core/offline/offlineMode';

function Tile({ leading, title, subtitle, onClick }) {
  const theme = useTheme();
  const c = theme.sli;
  return (
    <ListItemButton
      onClick={onClick}
      sx={{ bgcolor: c.button, borderRadius: '16px', p: 2, mb: 1.25, gap: 2 }}
    >
      {leading}
      <ListItemText
        primary={title}
        secondary={subtitle}
        primaryTypographyProps={{ sx: { color: c.text, fontSize: 18 } }}
        secondaryTypographyProps={{ sx: { color: `${c.text}B3`, fontSize: 13 } }}
      />
    </ListItemButton>
  );
}

export default function MeTab() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { user, profileUrl, isSuperAdmin } = useApp();

  const [testOpen, setTestOpen] = useState(false);
  const [uno, setUno] = useState('');
  const [syncing, setSyncing] = useState(false);
  const { show } = useMessage();

  const [manualOffline, setManualOffline] = useState(() => isManualOffline());
  const [noNetwork, setNoNetwork] = useState(() => hasNoNetwork());
  // Connectivity can change while this tab is open, so the subtitle follows it.
  useEffect(() => onOfflineChange(() => {
    setManualOffline(isManualOffline());
    setNoNetwork(hasNoNetwork());
  }), []);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const ok = await fullSync(Number(user?.UserId));
      const counts = await tableCounts();
      const at = await lastSyncedAt();
      const lines = Object.entries(counts).map(([t, n]) => `${t}: ${n}`).join('\n');
      show(
        ok ? 'Sync Complete' : 'Sync Finished (with issues)',
        `${lines}\n\nLast sync: ${at || '-'}`,
      );
    } finally {
      setSyncing(false);
    }
  };

  const masterUser = Number(user?.AllowTesting) === 1;
  const tuyaUser = Number(user?.AllowTestingTuya) === 1;

  const iconAvatar = (icon) => (
    <Avatar sx={{ bgcolor: c.back, color: c.text, width: 40, height: 40 }}>{icon}</Avatar>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back }}>
      <CenterTitleBar title="Account Controls" />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.875 }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <Tile
            leading={<Avatar src={profileUrl || undefined} sx={{ width: 40, height: 40 }} />}
            title="User Profile"
            onClick={() => navigate('/profile')}
          />

          <Tile
            leading={iconAvatar(<DashboardIcon />)}
            title="Wall Mode"
            onClick={() => navigate('/wall')}
          />

          {masterUser && (
            <Tile
              leading={iconAvatar(<AdminPanelSettingsIcon />)}
              title="Test Device"
              onClick={() => { setUno(''); setTestOpen(true); }}
            />
          )}

          {/* Flutter's "Sync now (local DB)" — forces a full pull and reports
              row counts. Without it there is no way to tell whether the offline
              cache actually filled, since a working cache looks exactly like a
              working network. Master users only. */}
          {masterUser && dbSupported() && (
            <Tile
              leading={iconAvatar(<SyncIcon />)}
              title="Sync now (local DB)"
              subtitle={syncing ? 'Syncing…' : 'Refresh the offline cache'}
              onClick={runSync}
            />
          )}

          {tuyaUser && (
            <Tile
              leading={iconAvatar(<DocumentScannerSharpIcon />)}
              title="Scan Devices"
              onClick={() => navigate('/add-device?tuya=1')}
            />
          )}

          {/* Offline Mode — the Dart's manual toggle. On, the app serves
              everything from the local cache and sends commands over Bluetooth
              instead of the internet. Useful on a bad connection, and for
              cutting data use. It also turns itself on when there is genuinely
              no network, which is why the subtitle reports the effective state
              rather than just the switch. */}
          <ListItemButton
            onClick={() => { setManual(!manualOffline); setManualOffline(!manualOffline); }}
            sx={{ bgcolor: c.button, borderRadius: '16px', p: 2, mb: 1.25, gap: 2 }}
          >
            {iconAvatar(<CloudOffIcon />)}
            <ListItemText
              primary="Offline Mode"
              secondary={
                // eslint-disable-next-line no-nested-ternary
                manualOffline
                  ? 'On — using local data and Bluetooth'
                  : (noNetwork ? 'Auto — no network detected' : 'Off')
              }
              primaryTypographyProps={{ sx: { color: c.text, fontSize: 18 } }}
              secondaryTypographyProps={{ sx: { color: `${c.text}B3`, fontSize: 13 } }}
            />
            <Switch
              checked={manualOffline}
              onChange={(e) => { setManual(e.target.checked); setManualOffline(e.target.checked); }}
              onClick={(e) => e.stopPropagation()}
            />
          </ListItemButton>

          {/* Onboarding a new device is easiest to find here, not buried in a
              room FAB — it is the first thing done with hardware out of the box. */}
          <Tile
            leading={iconAvatar(<WifiIcon />)}
            title="Set Up New Device"
            subtitle="Find a new device and give it your Wi-Fi"
            onClick={() => navigate('/wifi-setup')}
          />

          {/* Bring-up tool, not a user feature: it shows the raw BLE exchange
              and is only meaningful to someone debugging firmware. */}
          {isSuperAdmin && (
            <Tile
              leading={iconAvatar(<BluetoothSearchingIcon />)}
              title="BLE Test"
              subtitle="Scan, authenticate and watch the raw exchange"
              onClick={() => navigate('/ble-test')}
            />
          )}

          <Tile
            leading={iconAvatar(<QrCodeScannerIcon />)}
            title="Add Shared Device"
            subtitle="Scan a device's share QR to add it"
            onClick={() => navigate('/scan-share')}
          />

          <Tile
            leading={iconAvatar(<InfoIcon />)}
            title="About Us"
            onClick={() => navigate('/about')}
          />
        </Box>
      </Box>

      {/* userScreenFunctions.dart promptForUniqueNumber() */}
      <Dialog open={testOpen} onClose={() => setTestOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px', minWidth: 300 } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700, textAlign: 'center' }}>
          Enter Unique Number
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth placeholder="Type here..."
            value={uno} onChange={(e) => setUno(e.target.value)}
            sx={{ mt: 1, '& .MuiInputBase-input': { color: c.text } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)} sx={{ color: c.text }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!uno.trim()}
            onClick={() => { setTestOpen(false); navigate(`/device/${uno.trim()}`); }}
            sx={{ bgcolor: c.button, color: c.text }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
